import { useCallback, useMemo } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";
import {
  connectionRows,
  CreateIssueTypeDocument,
  type CreateIssueTypeInput,
  CustomFieldsListDataDocument,
  DeleteIssueTypeDocument,
  GetIssueTypeDocument,
  GetIssueTypesDocument,
  type IssueTypeFieldCustomField,
  type IssueTypeFieldRow,
  type IssueTypeRow,
  type IssueTypeWithFields,
  ReorderIssueTypeFieldDocument,
  SetDefaultIssueTypeDocument,
  UpdateIssueTypeDocument,
  type UpdateIssueTypeInput,
} from "#app/lib/data";
import { addIssueTypeToCache, removeIssueTypeFromCache } from "./issue-type-cache-utils";
import { toastMutationError } from "#app/lib/utils/toast-errors";

// The issue-type fragments under the names this feature reads them by. The
// schema already composes them by fragment spread, so these are aliases.
export type CustomFieldItem = IssueTypeFieldCustomField;
export type IssueTypeFieldItem = IssueTypeFieldRow;
export type IssueTypeItem = IssueTypeRow;
export type IssueTypeDetail = IssueTypeWithFields;

const ISSUE_TYPES_QUERY_VARS = { limit: 100 };
const CUSTOM_FIELDS_QUERY_VARS = { limit: 200, orderBy: { name: 1 } };

export function useIssueTypes() {
  const { data, loading, error } = useQuery(GetIssueTypesDocument, {
    variables: ISSUE_TYPES_QUERY_VARS,
    fetchPolicy: "cache-and-network",
  });

  const connection = data?.issueTypes;
  const issueTypes: IssueTypeItem[] = useMemo(
    () => connectionRows(connection),
    [connection],
  );

  return {
    issueTypes,
    total: connection?.total ?? 0,
    isLoading: loading,
    error,
  };
}

export function useIssueTypeMutations() {
  const apolloClient = useApolloClient();
  const [createMutation] = useMutation(CreateIssueTypeDocument);
  const [updateMutation] = useMutation(UpdateIssueTypeDocument);
  const [deleteMutation] = useMutation(DeleteIssueTypeDocument);
  const [setDefaultMutation] = useMutation(SetDefaultIssueTypeDocument);
  const [reorderFieldMutation] = useMutation(ReorderIssueTypeFieldDocument);

  const createIssueType = useCallback(
    async (input: CreateIssueTypeInput) => {
      try {
        const { data } = await createMutation({
          variables: { input },
          update(cache, { data }) {
            const result = data?.createIssueType;
            if (result?.__typename !== "IssueType") return;
            addIssueTypeToCache(cache, result);
          },
        });

        const result = data?.createIssueType;
        if (result?.__typename === "IssueType") {
          toastManager.add({ title: "Project type created", type: "success" });
          return result as IssueTypeItem;
        }
        toastManager.add({ title: "Failed to create project type", type: "error" });
        return null;
      } catch (err) {
        toastMutationError(err, "Failed to create project type");
        return null;
      }
    },
    [createMutation],
  );

  const updateIssueType = useCallback(
    async (id: string, input: UpdateIssueTypeInput) => {
      try {
        const { data } = await updateMutation({
          variables: { id, input },
        });

        const result = data?.updateIssueType;
        if (result?.__typename === "IssueType") {
          toastManager.add({ title: "Project type updated", type: "success" });
          return result as IssueTypeItem;
        }
        toastManager.add({ title: "Failed to update project type", type: "error" });
        return null;
      } catch (err) {
        toastMutationError(err, "Failed to update project type");
        return null;
      }
    },
    [updateMutation],
  );

  const deleteIssueType = useCallback(
    async (id: string, issueTypeId: string) => {
      try {
        await deleteMutation({
          variables: { id },
          update(cache, { data }) {
            const result = data?.deleteIssueType;
            if (result?.__typename !== "IssueType") return;
            removeIssueTypeFromCache(cache, result.issueTypeId);
            const cacheId = cache.identify(result);
            // `identify` answers `undefined` for an object it cannot key, and
            // the cache reads an absent `id` as the ROOT — the eviction would
            // drop a ROOT_QUERY field instead of this entity.
            if (cacheId) cache.evict({ id: cacheId });
            cache.gc();
          },
        });
        toastManager.add({ title: "Project type deleted", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to delete project type");
      }
    },
    [deleteMutation],
  );

  const setDefaultIssueType = useCallback(
    async (id: string) => {
      try {
        await setDefaultMutation({
          variables: { id },
          refetchQueries: [{ query: GetIssueTypesDocument, variables: ISSUE_TYPES_QUERY_VARS }],
        });
        toastManager.add({ title: "Default project type updated", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to update default project type");
      }
    },
    [setDefaultMutation],
  );

  const reorderIssueTypeField = useCallback(
    async (issueTypeId: string, customFieldId: string, sortOrder: number) => {
      await reorderFieldMutation({
        variables: { issueTypeId, customFieldId, sortOrder },
      });
    },
    [reorderFieldMutation],
  );

  // Optimistically updates a single junction row's sortOrder in the Apollo
  // cache and re-sorts the issue type's fields array, so the row repositions
  // before the network round-trip completes. Mirrors the stages pattern in
  // `use-stages-list.ts`.
  const optimisticReorderField = useCallback(
    (issueTypeId: string, customFieldId: string, sortOrder: number) => {
      // `identify` answers `undefined` for an object it cannot key, and the
      // cache reads an absent `id` as the ROOT — the edit would land on
      // ROOT_QUERY.
      const cacheId = apolloClient.cache.identify({
        __typename: "IssueType",
        issueTypeId,
      });
      if (!cacheId) return;

      apolloClient.cache.modify({
        id: cacheId,
        fields: {
          fields(existing: readonly any[] = []) {
            return [...existing]
              .map((row) =>
                row.customFieldId === customFieldId
                  ? { ...row, sortOrder }
                  : row,
              )
              .sort(
                (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
              );
          },
        },
      });
    },
    [apolloClient],
  );

  return {
    createIssueType,
    updateIssueType,
    deleteIssueType,
    setDefaultIssueType,
    reorderIssueTypeField,
    optimisticReorderField,
  };
}

export function useIssueType(id: string) {
  const variables = useMemo(() => ({ id }), [id]);
  const { data, loading, error } = useQuery(GetIssueTypeDocument, {
    variables,
    fetchPolicy: "cache-and-network",
    skip: !id,
  });

  // apiv2 returns `fields` as nullable; the IssueTypeDetail shape expects
  // an array. Coerce missing → []. Wider compatibility shim until callers
  // switch to a nullable shape.
  const issueType: IssueTypeDetail | null = data?.issueType
    ? { ...data.issueType, fields: data.issueType.fields ?? [] }
    : null;

  return { issueType, isLoading: loading, error };
}

export function useCustomFields() {
  const { data, loading, error } = useQuery(CustomFieldsListDataDocument, {
    variables: CUSTOM_FIELDS_QUERY_VARS,
    fetchPolicy: "cache-and-network",
  });

  const connection = data?.customFields;
  const customFields: CustomFieldItem[] = useMemo(
    () => connectionRows(connection),
    [connection],
  );

  return { customFields, isLoading: loading, error };
}
